import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import QuestionnaireComponent from './QuestionnaireComponent';
import ButtonComponent from './ButtonComponent';
import { areRequiredFieldsValid } from '../Utils/utils';
import ParagraphComponent from './ParagraphComponent';
import SpacerComponent from './SpacerComponent';
import DOMPurify from 'dompurify';

const DynamicQuestionnaireComponent = ({
  section,
  formData,
  readOnly,
  priorDecisions,
  updateFormData,
  handleButtonAction,
  onSubmit,
  onDynamicValidationChange,
  isTouchDevice,
  isMouseDevice
}) => {
  const [selectedOptions, setSelectedOptions] = useState({});
  const [followUpMap, setFollowUpMap] = useState({});
  const [hasInteracted, setHasInteracted] = useState(false);

  // Initialize from existing form data
  useEffect(() => {
    const newSelectedOptions = {};
    const newFollowUpMap = {};

    // Initialize selectedOptions and followUpMap from existing form data
    section.items.forEach((item) => {
      if (item.type === 'question' && formData[item.id]?.value) {
        const storedAnswer = formData[item.id];
        // Prefer value + label match for duplicate-coded options, then fallback to value.
        const selectedOption =
          item.options.find(option =>
            option.value === storedAnswer.value &&
            (!storedAnswer.label || option.label === storedAnswer.label)
          ) || item.options.find(option => option.value === storedAnswer.value);
        if (selectedOption) {
          newSelectedOptions[item.id] = selectedOption;
          
          // If this option has a followup, add it to the followUpMap
          if (selectedOption.followUp) {
            newFollowUpMap[item.id] = selectedOption.followUp;
          }
        }
      }
    });

    setSelectedOptions(newSelectedOptions);
    setFollowUpMap(newFollowUpMap);
  }, [section.items, formData]);

  useEffect(() => {
    const hasTextInput = section.items.some(
      (item) => item.type === 'textarea' && formData[item.id]?.value?.trim()
    );
    const hasOptionsSelected = Object.keys(selectedOptions).length > 0;

    setHasInteracted(hasOptionsSelected || hasTextInput);
  }, [selectedOptions, formData, section.items]);

  const clearFollowUpAnswers = (followUp) => {
    if (!followUp) return;
    updateFormData(followUp.id, '', '');
    followUp.options?.forEach((opt) => opt.followUp && clearFollowUpAnswers(opt.followUp));
  };

  const handleOptionSelect = (option, question) => {
    setHasInteracted(true);
    const isCurrentlySelected =
      selectedOptions[question.id]?.value === option.value &&
      selectedOptions[question.id]?.label === option.label;

    if (isCurrentlySelected) {
      setSelectedOptions((prev) => {
        const updatedOptions = { ...prev };
        delete updatedOptions[question.id];
        return updatedOptions;
      });

      setFollowUpMap((prev) => {
        const updatedMap = { ...prev };
        delete updatedMap[question.id];
        return updatedMap;
      });

      updateFormData(question.id, '', '');
      followUpMap[question.id] && clearFollowUpAnswers(followUpMap[question.id]);
    } else {
      updateFormData(question.id, option.value, option.label, question.metric, question.flowSheetId);
      setSelectedOptions((prev) => ({ ...prev, [question.id]: option }));
      setFollowUpMap((prev) =>
        option.followUp ? { ...prev, [question.id]: option.followUp } : prev
      );
    }
  };

  const handleTextChange = (e, id) => {
    setHasInteracted(true);
    updateFormData(id, e.target.value);
  };

  const handleFormSubmit = () => {
      onSubmit();
  };

  // Get all required fields including followup questions
  const getAllRequiredFields = () => {
    const baseRequiredFields = section.required || [];
    const followupRequiredFields = Object.values(followUpMap)
      .filter(followUp => followUp.required)
      .flatMap(followUp => followUp.required || []);
    
    const allRequiredFields = [...baseRequiredFields, ...followupRequiredFields];
    
    return allRequiredFields;
  };
  
  const isButtonDisabled = !areRequiredFieldsValid(formData, getAllRequiredFields());
  
  // Call onDynamicValidationChange callback if provided - use useEffect to avoid render-time calls
  useEffect(() => {
    if (onDynamicValidationChange) {
      // Pass section.id as the sectionId and !isButtonDisabled as isValid (since isButtonDisabled means NOT valid)
      onDynamicValidationChange(section.id || 'dynamic-questionnaire', !isButtonDisabled);
    }
  }, [isButtonDisabled, onDynamicValidationChange, section.id]);
  
  return (
    <VStack spacing={4} align="stretch" flex={1}>
      {section.items.map((item) => (
        <Box key={item.id}>
          {item.type === 'question' && (
            <>
              <QuestionnaireComponent
                question={{
                  ...item,
                  // When hideOthersOnSelect is true, collapse to only the selected option after selection
                  options: item.hideOthersOnSelect && selectedOptions[item.id]
                    ? item.options.filter(opt =>
                        opt.value === selectedOptions[item.id].value &&
                        opt.label === selectedOptions[item.id].label
                      )
                    : item.options,
                }}
                formData={formData}
                updateFormData={updateFormData}
                handleButtonAction={handleButtonAction}
                onOptionSelect={(option) => handleOptionSelect(option, item)}
              />
              {followUpMap[item.id] && ( 
                followUpMap[item.id].type === 'textarea' ? (
                  <FormControl mt={8}>
                    {followUpMap[item.id].label && (
                      <Box
                        as="label"
                        htmlFor={followUpMap[item.id].id}
                        display="block"
                        style={{
                          fontSize: `var(--${followUpMap[item.id].variant}-font-size, 16px)`,
                          fontWeight: `var(--${followUpMap[item.id].variant}-font-weight, 600)`,
                          color: `var(--${followUpMap[item.id].variant}-color, var(--sutter-sync-purple))`,
                        }}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(followUpMap[item.id].label) }}
                        mb={8}
                      />
                    )}
                    <Textarea
                      id={followUpMap[item.id].id}
                      name={followUpMap[item.id].name}
                      rows={followUpMap[item.id].row || 4}
                      placeholder={followUpMap[item.id].placeholder}
                      value={formData[followUpMap[item.id].id]?.value || ''}
                      onChange={(e) => handleTextChange(e, followUpMap[item.id].id)}
                      border="2px solid var(--sutter-sync-purple)"
                      borderRadius="6px"
                      mb={8}
                      style={{
                        fontFamily: `var(--${followUpMap[item.id].variant}-font-family, Roboto)`,
                        fontSize: `var(--${followUpMap[item.id].variant}-font-size, 16px)`,
                        fontWeight: `var(--${followUpMap[item.id].variant}-font-weight, 400)`,
                        lineHeight: `var(--${followUpMap[item.id].variant}-line-height, 150%)`,
                        letterSpacing: `var(--${followUpMap[item.id].variant}-letter-spacing, 0px)`,
                        backgroundColor: 'var(--chakra-colors-white)',
                        color: `var(--${followUpMap[item.id].variant}-color, #203433)`,
                      }}
                      _focus={{
                        border: "2px solid var(--sutter-sync-purple)",
                        boxShadow: "0 0 4px var(--sutter-sync-purple)",
                      }}
                      _hover={{
                        border: "2px solid var(--sutter-sync-purple)",
                        boxShadow: "0 0 4px var(--sutter-sync-purple)",
                      }}
                    />
                    {followUpMap[item.id].helperText && (
                      <Text
                        style={{
                          fontSize: `var(--${followUpMap[item.id].helperTextVariant || 'body-sm-min-reg'}-font-size)`,
                          fontWeight: `var(--${followUpMap[item.id].helperTextVariant || 'body-sm-min-reg'}-font-weight)`,
                          color: `var(--${followUpMap[item.id].helperTextVariant || 'body-sm-min-reg'}-color, var(--sutter-sync-purple))`,
                        }}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(followUpMap[item.id].helperText) }}
                      />
                    )}
                  </FormControl>
                ) : (
                  <QuestionnaireComponent
                    question={followUpMap[item.id]}
                    formData={formData}
                    updateFormData={updateFormData}
                    handleButtonAction={handleButtonAction}
                  />)
              )}
            </>
          )}

          {item.type === 'textarea' && (
            <FormControl mt={4} isRequired={section.required.includes(item.id)}>
              {item.label && (
                <FormLabel
                  htmlFor={item.id}
                  style={{
                    fontSize: `var(--${item.variant}-font-size, 16px)`,
                    fontWeight: `var(--${item.variant}-font-weight, 600)`,
                    color: `var(--${item.variant}-color, var(--sutter-sync-purple))`,
                  }}
                >
                {item.label}
                </FormLabel>
              )}
              <Textarea
                id={item.id}
                name={item.name}
                rows={item.row || 4}
                placeholder={item.placeholder}
                value={formData[item.id]?.value || ''}
                onChange={(e) => handleTextChange(e, item.id)}
                border="2px solid var(--sutter-sync-purple)"
                borderRadius="8px"
                style={{
                  fontFamily: `var(--${item.variant}-font-family, Roboto)`,
                  fontSize: `var(--${item.variant}-font-size, 16px)`,
                  fontWeight: `var(--${item.variant}-font-weight, 400)`,
                  lineHeight: `var(--${item.variant}-line-height, 150%)`,
                  letterSpacing: `var(--${item.variant}-letter-spacing, 0px)`,
                  backgroundColor: 'var(--chakra-colors-white)',
                  color: `var(--${item.variant}-color, #203433)`,
                }}
                _focus={{
                  border: "2px solid var(--sutter-sync-purple)",
                  boxShadow: "0 0 4px var(--sutter-sync-purple)",
                }}
                _hover={{
                  border: "2px solid var(--sutter-sync-purple)",
                  boxShadow: "0 0 4px var(--sutter-sync-purple)",
                }}
              />
              {item.helperText && (
                <Text
                  style={{
                    fontSize: `var(--${item.helperTextVariant || 'body-sm-min-reg'}-font-size)`,
                    fontWeight: `var(--${item.helperTextVariant || 'body-sm-min-reg'}-font-weight)`,
                    color: `var(--${item.helperTextVariant || 'body-sm-min-reg'}-color, var(--sutter-sync-purple))`,
                  }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.helperText) }}
                />
              )}
            </FormControl>
          )}
        </Box>
      ))}

      <SpacerComponent />

      <Box>
        {section.message && hasInteracted && (
          <ParagraphComponent
            content={section.message.content}
            variant={section.message.variant || 'body-md-min-reg'}
            ariaLabel={section.message.ariaLabel}
            role={section.message.role}
            color={section.message.color}
          />
        )}

        {!readOnly && section.button && (
          <ButtonComponent
            label={hasInteracted ? 'Submit' : 'Done'}
            variant={hasInteracted ? 'solid' : 'outline'}
            isDisabled={isButtonDisabled}
            onClick={handleFormSubmit}
          />
        )}
      </Box>
    </VStack>
  );
};

export default DynamicQuestionnaireComponent;